import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Mic, DollarSign, Calendar, ArrowRight, ArrowUpRight, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const INITIAL_GROUPS = 20;
const STEP = 20;

const titleCase = (s = "") => s.replace(/\S+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
const initials = (name = "") => name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "PT";
const fmtDos = (v) => {
  if (!v) return "—";
  const d = new Date(String(v).length === 10 ? v + "T12:00:00" : v);
  return isNaN(d) ? v : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const ageSex = (n) => {
  const a = n.patient_age != null && n.patient_age !== "" ? `${n.patient_age}y` : "";
  const s = (n.patient_gender || n.patient?.gender || "").toString().charAt(0).toUpperCase();
  return [a, s].filter(Boolean).join(" · ");
};
const nameOf = (n) => titleCase(n.patient_name || `${n.patient?.first_name || ""} ${n.patient?.last_name || ""}`.trim() || "Unknown patient");

// DQ-04 (resolved): the backend now cleans payer_name on read (strips any
// "LAST, FIRST - " subscriber prefix) and returns insurance.payer_canonical when a
// payer_id resolves. Prefer payer_canonical, else the (now clean) payer_name.
const payerInfo = (n, _patientName) => {
  const canon = (n && n.insurance && n.insurance.payer_canonical || "").trim();
  const text = canon || String((n && n.payer_name) || "").trim();
  const needsReview = !canon && !!(n && n.insurance && n.insurance.needs_review);
  return { text: text || "", needsReview };
};

// completed-coding readiness → band + per-claim denial reason (API-supported only)
const isReady = (n) => (n.claim_readiness?.status || "").toLowerCase() === "ready";
const whyOf = (n) => {
  const r = (n.claim_readiness?.denial_risk || "").toLowerCase();
  if (r === "high") return { txt: "High denial risk", cls: "rc-r rc-high" };
  if (r === "medium") return { txt: "Medium denial risk", cls: "rc-r" };
  return { txt: "No reason returned", cls: "rc-none" };
};
// child-row denial pill (V2 expanded rows): amber for medium/high, neutral outlined otherwise
const riskPill = (n) => {
  const r = (n.claim_readiness?.denial_risk || "").toLowerCase();
  if (r === "high") return { txt: "High risk", none: false };
  if (r === "medium") return { txt: "Medium risk", none: false };
  return { txt: "No reason returned", none: true };
};
const cptIcd = (n) => {
  const cpt = n.claim_readiness?.cpt_count ?? (Array.isArray(n.cpt_codes) ? n.cpt_codes.length : n.cpt_count) ?? 0;
  const icd = n.claim_readiness?.icd_count ?? (Array.isArray(n.icd_codes) ? n.icd_codes.length : n.icd_count) ?? 0;
  return `${cpt} CPT · ${icd} ICD`;
};

// patient shape the chart/dictate handlers understand (chart resolved on-demand in Patients.jsx)
const toPatient = (n) => ({
  full_name: nameOf(n),
  first_name: n.patient?.first_name || "",
  last_name: n.patient?.last_name || "",
  mrn: n.mrn || "",
  dob: n.patient_dob || n.patient?.date_of_birth || "",
  gender: n.patient_gender || n.patient?.gender || "",
  age: n.patient_age ?? "",
  chart_id: n.chart_id || null,
  chart_uuid: n.chart_uuid || null,
  client_id: n.client_id || "",
});

function Verbs({ p, onDictate, onFinance, onSchedule, i }) {
  return (
    <>
      <button className="rc-verb" onClick={() => onDictate(p)} data-testid={`recent-claim-dictate-${i}`}><Mic size={14} />Dictate</button>
      <button className="rc-verb" onClick={() => onFinance(p)} data-testid={`recent-claim-finance-${i}`}><DollarSign size={14} />Finance</button>
      <button className="rc-verb" onClick={() => onSchedule(p)} data-testid={`recent-claim-schedule-${i}`}><Calendar size={14} />Schedule</button>
    </>
  );
}

export default function RecentClaims({ onOpenChart, onDictate, onFinance, onSchedule, onOpenClaim, resolveChart, v2 = false }) {
  const [rows, setRows] = useState([]);
  const [hiddenNoDos, setHiddenNoDos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(() => new Set());
  const [visible, setVisible] = useState(INITIAL_GROUPS);
  // V2 collapsible bands + independent per-band pagination (10/25/50).
  const [bandOpen, setBandOpen] = useState({ review: true, ready: true });
  const [rpp, setRpp] = useState({ review: 10, ready: 10 });
  const [page, setPage] = useState({ review: 1, ready: 1 });
  // Option C — notes rarely resolve to a chart (DQ-03). Open claim is the primary,
  // always-reliable action; the chart-dependent actions (Open chart / Dictate /
  // Finance / Schedule) are resolved lazily on hover and shown only when a chart
  // actually exists — otherwise a muted "No linked chart". Keyed by content so a
  // reload can't mis-map a cached result.
  const [chartState, setChartState] = useState({});
  const resolvedRef = React.useRef({});
  const ensureChart = useCallback(async (key, patient) => {
    if (resolvedRef.current[key]) return;
    // DQ-03 (resolved): notes now carry chart_uuid / chart_id. When the row is
    // already linked, use it directly — no fragile name lookup. Only un-linked
    // rows (null link, e.g. pre-backfill or pasted notes) fall back to resolveChart.
    const linked = patient && (patient.chart_uuid || patient.chart_id);
    if (linked) {
      resolvedRef.current[key] = true;
      setChartState((s) => ({ ...s, [key]: { status: "ok", uuid: patient.chart_uuid || patient.chart_id } }));
      return;
    }
    if (!resolveChart) return;
    resolvedRef.current[key] = true;
    setChartState((s) => ({ ...s, [key]: { status: "pending" } }));
    try {
      const uuid = await resolveChart(patient);
      setChartState((s) => ({ ...s, [key]: uuid ? { status: "ok", uuid } : { status: "none" } }));
    } catch { setChartState((s) => ({ ...s, [key]: { status: "none" } })); }
  }, [resolveChart]);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const PAGE = 200; let offset = 0; let all = []; let total = Infinity;
      for (let i = 0; i < 25 && all.length < total; i++) {
        const { data } = await api.get("/api/edi/note-to-claim/notes", { params: { limit: PAGE, offset } });
        const list = Array.isArray(data?.notes) ? data.notes : [];
        total = data?.total ?? list.length;
        if (!list.length) break;
        all = all.concat(list);
        offset += PAGE;
        if (list.length < PAGE) break;
      }
      const isExcluded = (v) => (Array.isArray(v) ? v.length > 0 : !!v);
      const done = all.filter((n) => (n.status || "").toLowerCase() === "ready" && !isExcluded(n.excluded_historical));
      const withDos = done.filter((n) => n.date_of_service);
      withDos.sort((a, b) =>
        String(b.date_of_service).localeCompare(String(a.date_of_service)) ||
        String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setRows(withDos);
      setHiddenNoDos(done.length - withDos.length);
      setVisible(INITIAL_GROUPS);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Couldn't load recent claims.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { reviewCount, readyCount, ordered } = useMemo(() => {
    const review = rows.filter((n) => !isReady(n));
    const ready = rows.filter((n) => isReady(n));
    const groupBy = (list) => {
      const groups = []; const idx = {};
      for (const n of list) {
        const k = `${nameOf(n)}|${n.date_of_service}`;
        if (idx[k] == null) { idx[k] = groups.length; groups.push([]); }
        groups[idx[k]].push(n);
      }
      return groups;
    };
    const ord = [
      ...groupBy(review).map((g) => ({ band: "review", g })),
      ...groupBy(ready).map((g) => ({ band: "ready", g })),
    ];
    return { reviewCount: review.length, readyCount: ready.length, ordered: ord };
  }, [rows]);

  const shown = ordered.slice(0, visible);
  const reviewGroups = useMemo(() => ordered.filter((o) => o.band === "review"), [ordered]);
  const readyGroups = useMemo(() => ordered.filter((o) => o.band === "ready"), [ordered]);
  const toggle = (key) => setOpen((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const renderRow = (g, band, gi) => {
    const first = g[0];
    const p = toPatient(first);
    const pInfo = payerInfo(first, nameOf(first));
    const payerCell = pInfo.needsReview
      ? <div className="rc-payer"><span className="rc-p rc-review" title="Payer extraction is low-confidence — needs review">Payer needs review</span></div>
      : pInfo.text
      ? <div className="rc-payer"><span className="rc-p" title={pInfo.text}>{titleCase(pInfo.text)}</span></div>
      : <div className="rc-payer"><span className="rc-p rc-absent">No payer on file</span></div>;

    if (g.length === 1) {
      const w = band === "ready" ? null : whyOf(first);
      return (
        <div className="rc-row" key={first.id || gi} data-testid={`recent-claim-row-${gi}`}>
          <span className="rc-dos">{fmtDos(first.date_of_service)}</span>
          <div className="rc-pt"><span className="rc-av">{initials(nameOf(first))}</span><div className="rc-nm"><b>{nameOf(first)}</b><span>{ageSex(first)}</span></div></div>
          {payerCell}
          <span className="rc-codes">{cptIcd(first)}</span>
          <div className="rc-why">{w && <span className={w.cls}>{w.txt}</span>}</div>
          <span className="rc-vis">{first.visit_id || ""}</span>
          <div className="rc-acts">
            <Verbs p={p} onDictate={onDictate} onFinance={onFinance} onSchedule={onSchedule} i={gi} />
            <button className="rc-go" onClick={() => onOpenChart(p)} data-testid={`recent-claim-open-chart-${gi}`}>Open chart <ArrowRight size={14} /></button>
          </div>
        </div>
      );
    }

    // encounter group: parent + expandable codings
    const key = `g-${gi}`;
    const isOpen = open.has(key);
    const flagged = g.filter((n) => ["medium", "high"].includes((n.claim_readiness?.denial_risk || "").toLowerCase())).length;
    const silent = g.length - flagged;
    return (
      <div className={`rc-grp ${isOpen ? "open" : ""}`} key={key}>
        <div className="rc-row" data-testid={`recent-claim-row-${gi}`}>
          <span className="rc-dos">{fmtDos(first.date_of_service)}</span>
          <div className="rc-pt">
            <span className="rc-av">{initials(nameOf(first))}</span>
            <div className="rc-nm">
              <b>{nameOf(first)}</b><span>{ageSex(first)}</span>
              <button className="rc-more" aria-expanded={isOpen} onClick={() => toggle(key)} data-testid={`recent-claim-group-toggle-${gi}`}>
                <ChevronRight size={11} />{g.length} codings of this visit
              </button>
            </div>
          </div>
          {payerCell}
          <span className="rc-codes">{g.length} claims</span>
          <div className="rc-why">
            {band !== "ready" && (
              <span className="rc-sumwhy">{flagged ? `${flagged} flagged for denial risk` : ""}{silent ? <span>{silent} no reason returned</span> : null}</span>
            )}
          </div>
          <span className="rc-vis" />
          <div className="rc-acts">
            <Verbs p={p} onDictate={onDictate} onFinance={onFinance} onSchedule={onSchedule} i={gi} />
            <button className="rc-go" onClick={() => onOpenChart(p)} data-testid={`recent-claim-open-chart-${gi}`}>Open chart <ArrowRight size={14} /></button>
          </div>
        </div>
        {isOpen && (
          <div className="rc-kids" data-testid={`recent-claim-kids-${gi}`}>
            {g.map((k, ki) => {
              const w = band === "ready" ? null : whyOf(k);
              return (
                <div className="rc-row rc-kid" key={k.id || ki}>
                  <span className="rc-dos">{fmtDos(k.date_of_service)}</span>
                  <div className="rc-pt"><span className="rc-av">{initials(nameOf(k))}</span><div className="rc-nm"><b>{nameOf(k)}</b><span>{ageSex(k)}</span></div></div>
                  {(() => { const ki2 = payerInfo(k, nameOf(k)); return ki2.needsReview ? <div className="rc-payer"><span className="rc-p rc-review" title="Payer extraction is low-confidence — needs review">Payer needs review</span></div> : ki2.text ? <div className="rc-payer"><span className="rc-p" title={ki2.text}>{titleCase(ki2.text)}</span></div> : <div className="rc-payer"><span className="rc-p rc-absent">No payer on file</span></div>; })()}
                  <span className="rc-codes">{cptIcd(k)}</span>
                  <div className="rc-why">{w && <span className={w.cls}>{w.txt}</span>}</div>
                  <span className="rc-vis">{k.visit_id || ""}</span>
                  <div className="rc-acts">
                    <button className="rc-go" onClick={() => onOpenClaim(g, ki)} data-testid={`recent-claim-open-claim-${gi}-${ki}`}>Open claim <ArrowRight size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // V2-only expanded-row presentation (Patients V2 page). Same data/grouping/sorting.
  const renderRowV2 = (g, band, gi) => {
    const first = g[0];
    const p = toPatient(first);
    const rkey = `${g.length === 1 ? "s" : "g"}-${first.id || (nameOf(first) + "|" + first.date_of_service)}`;
    const cs = chartState[rkey];
    const ep = cs && cs.uuid ? { ...p, chart_uuid: cs.uuid } : p;
    const pInfo = payerInfo(first, nameOf(first));
    const payerCell = pInfo.needsReview
      ? <span className="rc2-muted rc2-trunc rc2-review" title="Payer extraction is low-confidence — needs review">Payer needs review</span>
      : pInfo.text
      ? <span className="rc2-muted rc2-trunc" title={pInfo.text}>{titleCase(pInfo.text)}</span>
      : <span className="rc2-absent">No payer on file</span>;
    const nameCell = (
      <span className="rc2-nm">
        <span className="rc2-av">{initials(nameOf(first))}</span>
        <span className="rc2-nmtxt"><b className="rc2-trunc">{nameOf(first)}</b><span className="rc2-faint">{ageSex(first)}</span></span>
      </span>
    );
    // Chart-dependent verbs reveal INLINE on hover (opacity only — the cell width is
    // fixed by the grid, so revealing them never changes row height and moving the
    // pointer across rows never reflows the list). Shown only when a chart resolves.
    const verbs = cs && cs.status === "ok" ? (
      <span className="rc2-verbs">
        <button title="Dictate" onClick={() => onDictate(ep)} data-testid={`recent-claim-dictate-${gi}`}><Mic size={14} /></button>
        <button title="Finance" onClick={() => onFinance(ep)} data-testid={`recent-claim-finance-${gi}`}><DollarSign size={14} /></button>
        <button title="Schedule" onClick={() => onSchedule(ep)} data-testid={`recent-claim-schedule-${gi}`}><Calendar size={14} /></button>
        <button title="Open chart" onClick={() => onOpenChart(ep)} data-testid={`recent-claim-open-chart-${gi}`}><ArrowUpRight size={14} /></button>
      </span>
    ) : null;
    const primaryClaimBtn = (
      <span className="rc2-act">
        {verbs}
        <button className="rc2-btn" onClick={() => onOpenClaim(g, 0)} data-testid={`recent-claim-open-claim-${gi}`}>Open claim <ArrowRight size={13} /></button>
      </span>
    );

    if (g.length === 1) {
      const rp = band === "ready" ? null : riskPill(first);
      return (
        <div className="rc2-group" key={first.id || gi} onMouseEnter={() => ensureChart(rkey, p)}>
          <div className="rc2-row rc2-parent" data-testid={`recent-claim-row-${gi}`}>
            <span />
            <span className="rc2-muted">{fmtDos(first.date_of_service)}</span>
            {nameCell}
            {payerCell}
            <span className="rc2-muted">{cptIcd(first)}</span>
            <span className="rc2-risk">{rp ? <span className={`rc2-pill ${rp.none ? "rc2-none" : ""}`}>{rp.txt}</span> : null}</span>
            {primaryClaimBtn}
          </div>
        </div>
      );
    }

    const key = `g-${gi}`;
    const isOpen = open.has(key);
    const flagged = g.filter((n) => ["medium", "high"].includes((n.claim_readiness?.denial_risk || "").toLowerCase())).length;
    const silent = g.length - flagged;
    return (
      <div className="rc2-group" key={key} onMouseEnter={() => ensureChart(rkey, p)}>
        <div className="rc2-row rc2-parent" data-testid={`recent-claim-row-${gi}`}>
          <button className="rc2-tog" aria-expanded={isOpen} aria-label={isOpen ? "Collapse codings" : "Expand codings"} onClick={() => toggle(key)} data-testid={`recent-claim-group-toggle-${gi}`}>
            <ChevronRight size={14} className="rc2-chev" />
          </button>
          <span className="rc2-muted">{fmtDos(first.date_of_service)}</span>
          {nameCell}
          {payerCell}
          <span className="rc2-muted">{g.length} codings</span>
          <span className="rc2-risk">
            {band !== "ready" && (
              <>
                {flagged > 0 && <span className="rc2-amb">{flagged} at risk</span>}
                {silent > 0 && <span className="rc2-faint">{flagged > 0 ? " · " : ""}{silent} no reason</span>}
              </>
            )}
          </span>
          {primaryClaimBtn}
        </div>
        {isOpen && g.map((k, ki) => {
          const rp = band === "ready" ? null : riskPill(k);
          return (
            <div className="rc2-row rc2-child" key={k.id || ki} data-testid={`recent-claim-kid-${gi}-${ki}`}>
              <span className="rc2-rail" />
              <span />
              <span className="rc2-muted rc2-trunc">Coding {ki + 1}{k.visit_id ? <span className="rc2-vis">{k.visit_id}</span> : null}</span>
              <span />
              <span className="rc2-muted">{cptIcd(k)}</span>
              <span className="rc2-risk">{rp ? <span className={`rc2-pill ${rp.none ? "rc2-none" : ""}`}>{rp.txt}</span> : null}</span>
              <span className="rc2-act"><button className="rc2-lnkbtn" onClick={() => onOpenClaim(g, ki)} data-testid={`recent-claim-open-claim-${gi}-${ki}`}>Open claim <ArrowRight size={13} /></button></span>
            </div>
          );
        })}
      </div>
    );
  };

  // A status band: a collapse/expand button, its table, and an independent pager.
  const renderBandV2 = (band, groups) => {
    const isOpen = bandOpen[band];
    const per = rpp[band];
    const totalRows = groups.length;
    const pages = Math.max(1, Math.ceil(totalRows / per));
    const cur = Math.min(page[band], pages);
    const start = (cur - 1) * per;
    const slice = groups.slice(start, start + per);
    const label = band === "review" ? "Needs review" : "Ready";
    return (
      <div className="rc2-section" key={band} data-testid={`recent-claims-section-${band}`}>
        <button className="rc-band rc-bandbtn" aria-expanded={isOpen} onClick={() => setBandOpen((s) => ({ ...s, [band]: !s[band] }))} data-testid={`recent-claims-band-toggle-${band}`}>
          <ChevronRight size={13} className="rc-bandchev" />
          {band === "review" && <span className="rc-dot" aria-hidden="true" />}
          {label}
          <span className="rc-n">{totalRows}</span>
          {!isOpen && <span className="rc-collapsed">collapsed</span>}
        </button>
        {isOpen && (
          <>
            <div className="rc2-scroll"><div className="rc2-inner">
              <div className="rc2-row rc2-hd" aria-hidden="true"><span /><span>Date of service</span><span>Patient</span><span>Payer</span><span>Claims</span><span>Denial risk</span><span /></div>
              {slice.map((item, i) => renderRowV2(item.g, band, `${band}-${start + i}`))}
            </div></div>
            <div className="rc-pager" data-testid={`recent-claims-pager-${band}`}>
              <span className="rc-pgcount">Showing {totalRows === 0 ? 0 : start + 1}–{Math.min(start + per, totalRows)} of {totalRows}</span>
              <div className="rc-pgright">
                <label className="rc-rpp">Rows
                  <select value={per} onChange={(e) => { const v = Number(e.target.value); setRpp((s) => ({ ...s, [band]: v })); setPage((s) => ({ ...s, [band]: 1 })); }} data-testid={`recent-claims-rpp-${band}`}>
                    <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                  </select>
                </label>
                <div className="rc-pgbtns">
                  <button disabled={cur <= 1} onClick={() => setPage((s) => ({ ...s, [band]: cur - 1 }))} data-testid={`recent-claims-prev-${band}`} aria-label="Previous page">‹</button>
                  {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                    <button key={n} className={n === cur ? "on" : ""} onClick={() => setPage((s) => ({ ...s, [band]: n }))} data-testid={`recent-claims-page-${band}-${n}`}>{n}</button>
                  ))}
                  <button disabled={cur >= pages} onClick={() => setPage((s) => ({ ...s, [band]: cur + 1 }))} data-testid={`recent-claims-next-${band}`} aria-label="Next page">›</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <section className={`rqc ${v2 ? "rqc-v2" : ""}`} data-testid="recent-claims">
      <style>{RQC_CSS + (v2 ? RQC_V2_CSS : "")}</style>
      <div className="rc-cardhead">
        <span className="rc-t">Recent claims</span>
        <span className="rc-note">
          {loading ? "loading…" : `${rows.length} coded ${rows.length === 1 ? "claim" : "claims"} · newest first${hiddenNoDos ? ` · ${hiddenNoDos} without a DOS hidden` : ""}`}
        </span>
      </div>

      {loading ? (
        <div className="rc-empty" data-testid="recent-claims-loading"><Loader2 className="rc-spin" size={18} /> Loading coded claims…</div>
      ) : err ? (
        <div className="rc-empty rc-err" data-testid="recent-claims-error">{err} <button className="rc-retry" onClick={load}>Retry</button></div>
      ) : rows.length === 0 ? (
        <div className="rc-empty" data-testid="recent-claims-empty">No coded claims with a date of service yet.</div>
      ) : v2 ? (
        <div className="rc2-sections" data-testid="recent-claims-list">
          {renderBandV2("review", reviewGroups)}
          {renderBandV2("ready", readyGroups)}
        </div>
      ) : (
        <div className="rc-card">
          <div className="rc-cols" aria-hidden="true">
            <span>DOS</span><span>Patient</span><span>Payer</span><span>Codes</span><span>Review</span><span>Visit</span><span className="rc-end" />
          </div>
          <div data-testid="recent-claims-list">
            {(() => {
              const out = []; let lastBand = null; let rowsBuf = [];
              const flush = () => { if (rowsBuf.length) { out.push(<div className="rc-rows" key={`rows-${out.length}`}>{rowsBuf}</div>); rowsBuf = []; } };
              shown.forEach((item, i) => {
                if (item.band !== lastBand) {
                  flush();
                  lastBand = item.band;
                  out.push(
                    <div className="rc-band" key={`band-${item.band}`} data-testid={`recent-claims-band-${item.band}`}>
                      {item.band === "review" && <span className="rc-dot" aria-hidden="true" />}
                      {item.band === "review" ? "Needs review" : "Ready"}
                      <span className="rc-n">{item.band === "review" ? reviewCount : readyCount}</span>
                    </div>
                  );
                }
                rowsBuf.push(renderRow(item.g, item.band, i));
              });
              flush();
              return out;
            })()}
          </div>
          {ordered.length > visible && (
            <div className="rc-loadwrap">
              <button className="rc-loadmore" onClick={() => setVisible((v) => v + STEP)} data-testid="recent-claims-load-more">
                Load {Math.min(STEP, ordered.length - visible)} more
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const RQC_CSS = `
.rqc{--ink:#141412;--sub:#6C6C69;--faint:#6E6E6B;--mute:#9B9B97;--hair:#E9E9EC;--soft:#F1F1F4;--softer:#FBFBFD;--chrome:#fff;--amber:#FF9500;--amber-txt:#9A5A00;--vio:#5856D6;--vio-txt:#4A4AC2;--hover:linear-gradient(90deg,rgba(88,86,214,.08),rgba(88,86,214,.02));--mono:ui-monospace,"SF Mono",Menlo,Consolas,monospace;padding-top:18px;}
.rqc .rc-cardhead{display:flex;align-items:baseline;gap:12px;padding-bottom:10px;flex-wrap:wrap;}
.rqc .rc-t{font-weight:700;font-size:13px;color:var(--ink);}
.rqc .rc-note{color:var(--faint);font-size:12px;}
.rqc .rc-card{background:var(--chrome);border:1px solid var(--hair);border-radius:10px;overflow:hidden;}
.rqc .rc-cols,.rqc .rc-row{display:grid;grid-template-columns:88px minmax(110px,1.2fr) minmax(0,1.3fr) 84px minmax(0,1fr) 108px 300px;column-gap:12px;align-items:center;}
.rqc .rc-cols{padding:8px 16px;background:var(--softer);border-bottom:1px solid var(--hair);color:var(--faint);font-size:9.5px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;}
.rqc .rc-cols .rc-end{text-align:right;}
.rqc .rc-band{display:flex;align-items:center;gap:8px;padding:11px 16px 8px;background:var(--soft);border-bottom:1px solid var(--hair);font-weight:600;font-size:12.5px;color:var(--ink);}
.rqc .rc-band .rc-n{color:var(--faint);font-weight:400;font-variant-numeric:tabular-nums;}
.rqc .rc-rows{border-bottom:1px solid var(--hair);}
.rqc .rc-rows:last-child{border-bottom:0;}
.rqc .rc-dot{width:6px;height:6px;border-radius:50%;background:var(--amber);flex:none;}
.rqc .rc-row{position:relative;padding:8px 16px;min-height:52px;border-top:1px solid var(--hair);}
.rqc .rc-rows > .rc-row:first-child,.rqc .rc-rows > .rc-grp:first-child > .rc-row:first-child{border-top:0;}
.rqc .rc-row:hover,.rqc .rc-row:focus-within{background:var(--hover);}
.rqc .rc-dos{font-variant-numeric:tabular-nums;color:var(--faint);font-size:12.5px;white-space:nowrap;}
.rqc .rc-pt{display:flex;align-items:center;gap:12px;min-width:0;}
.rqc .rc-av{width:28px;height:28px;border-radius:50%;background:var(--soft);border:1px solid var(--hair);display:grid;place-items:center;font-size:9.5px;font-weight:600;color:var(--sub);flex:none;}
.rqc .rc-nm{min-width:0;}
.rqc .rc-nm b{display:block;font-weight:600;font-size:13px;color:var(--ink);white-space:normal;overflow-wrap:anywhere;line-height:1.25;}
.rqc .rc-nm span{display:block;color:var(--faint);font-size:10.5px;}
.rqc .rc-payer{min-width:0;}
.rqc .rc-payer .rc-p{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--sub);font-size:12.5px;}
.rqc .rc-payer .rc-p.rc-absent{color:var(--amber-txt);}
.rqc .rc-payer .rc-p.rc-review{color:var(--amber-txt);font-style:italic;}
.rqc .rc-codes{color:var(--faint);font-variant-numeric:tabular-nums;font-size:12.5px;white-space:nowrap;}
.rqc .rc-why{font-size:12.5px;}
.rqc .rc-why .rc-r{color:var(--amber-txt);font-weight:600;}
.rqc .rc-why .rc-r.rc-high{color:#B42318;}
.rqc .rc-why .rc-none{color:var(--faint);}
.rqc .rc-vis{font-family:var(--mono);font-size:10.5px;color:var(--faint);white-space:nowrap;}
.rqc .rc-acts{display:flex;align-items:center;justify-content:flex-end;gap:4px;}
.rqc .rc-verb{border:0;background:none;padding:0 7px;height:28px;border-radius:6px;color:var(--sub);font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px;opacity:0;transition:opacity .12s;}
.rqc .rc-verb:hover{color:var(--ink);background:var(--soft);}
.rqc .rc-row:hover .rc-verb,.rqc .rc-row:focus-within .rc-verb{opacity:1;}
@media(hover:none){.rqc .rc-verb{opacity:1;}}
.rqc .rc-go{height:28px;padding:0 11px;border:1px solid var(--hair);border-radius:8px;background:var(--chrome);font-size:12px;font-weight:600;color:var(--ink);cursor:pointer;display:inline-flex;align-items:center;gap:6px;white-space:nowrap;margin-left:4px;transition:border-color .12s,color .12s;}
.rqc .rc-go:hover{border-color:var(--vio);color:var(--vio-txt);}
.rqc .rc-more{border:0;background:none;padding:0;margin-top:2px;color:var(--vio-txt);font-size:10.5px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:4px;}
.rqc .rc-more svg{transition:transform .2s;}
.rqc .rc-grp.open .rc-more svg{transform:rotate(90deg);}
.rqc .rc-kids{background:var(--softer);}
.rqc .rc-kid{min-height:40px;}
.rqc .rc-kid .rc-dos,.rqc .rc-kid .rc-pt,.rqc .rc-kid .rc-payer{visibility:hidden;}
.rqc .rc-kid .rc-codes::before{content:"";display:inline-block;width:10px;height:1px;background:var(--hair);vertical-align:middle;margin-right:8px;}
.rqc .rc-sumwhy{font-size:12.5px;color:var(--amber-txt);font-weight:600;}
.rqc .rc-sumwhy span{display:block;color:var(--faint);font-weight:400;}
.rqc .rc-loadwrap{display:flex;justify-content:center;padding:12px;border-top:1px solid var(--hair);background:var(--softer);}
.rqc .rc-loadmore{border:1px solid var(--hair);background:var(--chrome);border-radius:8px;padding:7px 16px;font-size:12px;font-weight:600;color:var(--ink);cursor:pointer;}
.rqc .rc-loadmore:hover{border-color:var(--vio);color:var(--vio-txt);}
.rqc .rc-empty{border:1px dashed rgba(0,0,0,.14);background:rgba(0,0,0,.015);border-radius:12px;padding:34px;text-align:center;font-size:13px;color:var(--sub);display:flex;align-items:center;justify-content:center;gap:9px;}
.rqc .rc-err{color:#B42318;}
.rqc .rc-retry{margin-left:8px;border:1px solid var(--hair);background:#fff;border-radius:7px;padding:4px 11px;font-size:12px;font-weight:600;cursor:pointer;color:var(--ink);}
.rqc .rc-spin{animation:rqcspin .8s linear infinite;}
@keyframes rqcspin{to{transform:rotate(360deg);}}
@media(max-width:980px){
  .rqc .rc-cols{display:none;}
  .rqc .rc-row{grid-template-columns:1fr auto;row-gap:2px;padding:12px 16px;}
  .rqc .rc-dos{grid-column:1/-1;}
  .rqc .rc-pt{grid-column:1;}
  .rqc .rc-acts{grid-column:2;grid-row:2;}
  .rqc .rc-payer,.rqc .rc-codes,.rqc .rc-why,.rqc .rc-vis{grid-column:1/-1;padding-left:40px;}
  .rqc .rc-kid .rc-dos,.rqc .rc-kid .rc-pt,.rqc .rc-kid .rc-payer{display:none;}
  .rqc .rc-verb{display:none;}
}
`;


// V2-only expanded-row layout (Patients V2). Scoped under .rqc.rqc-v2, uses fresh rc2-* classes
// so the original Patients page (no v2 prop) is untouched. Presentation only.
const RQC_V2_CSS = `
.rqc.rqc-v2 .rc2-sections{display:flex;flex-direction:column;gap:16px;}
.rqc.rqc-v2 .rc2-section{border:1px solid var(--hair);border-radius:10px;overflow:hidden;background:var(--chrome);}
.rqc.rqc-v2 .rc2-scroll{overflow-x:auto;}
.rqc.rqc-v2 .rc2-inner{min-width:820px;}
.rqc.rqc-v2 .rc-band{display:flex;align-items:center;gap:8px;padding:11px 16px;background:var(--soft);border-bottom:1px solid var(--hair);font-weight:600;font-size:12.5px;color:var(--ink);}
.rqc.rqc-v2 .rc-bandbtn{width:100%;border:0;text-align:left;cursor:pointer;font:inherit;font-weight:600;}
.rqc.rqc-v2 .rc-bandchev{color:var(--mute);transition:transform .18s;flex:none;}
.rqc.rqc-v2 .rc-bandbtn[aria-expanded="true"] .rc-bandchev{transform:rotate(90deg);}
.rqc.rqc-v2 .rc-band .rc-n{color:var(--faint);font-weight:400;font-variant-numeric:tabular-nums;}
.rqc.rqc-v2 .rc-dot{width:6px;height:6px;border-radius:50%;background:var(--amber);flex:none;}
.rqc.rqc-v2 .rc-collapsed{margin-left:6px;font-size:11px;color:var(--mute);font-style:italic;font-weight:400;}
.rqc.rqc-v2 .rc2-row{display:grid;grid-template-columns:26px 100px minmax(120px,1.2fr) minmax(90px,1fr) 84px minmax(110px,1fr) 210px;align-items:center;column-gap:12px;padding:0 16px;border-top:1px solid var(--hair);}
.rqc.rqc-v2 .rc2-hd{min-height:30px;font-size:10px;letter-spacing:.04em;text-transform:uppercase;color:var(--mute);border-top:0;background:var(--softer);}
.rqc.rqc-v2 .rc2-hd span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.rqc.rqc-v2 .rc2-parent{min-height:60px;}
.rqc.rqc-v2 .rc2-parent:hover{background:var(--hover);}
.rqc.rqc-v2 .rc2-child{min-height:40px;background:var(--softer);}
.rqc.rqc-v2 .rc2-child:hover{background:var(--soft);}
.rqc.rqc-v2 .rc2-muted{color:var(--sub);font-size:12.5px;min-width:0;}
.rqc.rqc-v2 .rc2-faint{color:var(--faint);font-size:12px;}
.rqc.rqc-v2 .rc2-nm{display:flex;align-items:center;gap:8px;min-width:0;}
.rqc.rqc-v2 .rc2-nmtxt{min-width:0;}
.rqc.rqc-v2 .rc2-av{width:28px;height:28px;border-radius:50%;background:var(--soft);border:1px solid var(--hair);display:flex;align-items:center;justify-content:center;font-size:9.5px;font-weight:600;color:var(--sub);flex:none;}
.rqc.rqc-v2 .rc2-nm b{font-weight:600;font-size:13px;color:var(--ink);display:block;}
.rqc.rqc-v2 .rc2-trunc{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;display:block;}
.rqc.rqc-v2 .rc2-absent{color:var(--amber-txt);font-size:12.5px;white-space:nowrap;}
.rqc.rqc-v2 .rc2-review{color:var(--amber-txt);font-style:italic;}
.rqc.rqc-v2 .rc2-tog{border:1px solid var(--hair);background:transparent;border-radius:6px;width:24px;height:24px;display:flex;align-items:center;justify-content:center;color:var(--sub);cursor:pointer;padding:0;}
.rqc.rqc-v2 .rc2-tog:hover{border-color:var(--mute);color:var(--ink);}
.rqc.rqc-v2 .rc2-chev{transition:transform .18s;}
.rqc.rqc-v2 .rc2-tog[aria-expanded="true"] .rc2-chev{transform:rotate(90deg);}
.rqc.rqc-v2 .rc2-risk{white-space:nowrap;font-size:12.5px;overflow:hidden;text-overflow:ellipsis;}
.rqc.rqc-v2 .rc2-amb{color:#BA7517;font-weight:600;}
.rqc.rqc-v2 .rc2-pill{display:inline-block;font-size:11px;padding:2px 8px;border-radius:999px;background:#FAEEDA;color:#854F0B;white-space:nowrap;}
.rqc.rqc-v2 .rc2-pill.rc2-none{background:transparent;color:var(--sub);border:1px solid var(--hair);}
.rqc.rqc-v2 .rc2-act{display:flex;align-items:center;justify-content:flex-end;gap:6px;min-width:0;}
.rqc.rqc-v2 .rc2-verbs{display:inline-flex;align-items:center;gap:2px;opacity:0;pointer-events:none;transition:opacity .12s;}
.rqc.rqc-v2 .rc2-group:hover .rc2-verbs,.rqc.rqc-v2 .rc2-parent:focus-within .rc2-verbs{opacity:1;pointer-events:auto;}
@media(hover:none){.rqc.rqc-v2 .rc2-verbs{opacity:1;pointer-events:auto;}}
.rqc.rqc-v2 .rc2-verbs button{width:28px;height:28px;display:inline-flex;align-items:center;justify-content:center;border:1px solid transparent;background:none;border-radius:7px;color:var(--sub);cursor:pointer;padding:0;}
.rqc.rqc-v2 .rc2-verbs button:hover{background:var(--soft);color:var(--ink);border-color:var(--hair);}
.rqc.rqc-v2 .rc2-btn{border:1px solid var(--mute);border-radius:8px;padding:6px 10px;background:var(--chrome);display:inline-flex;align-items:center;gap:6px;font:inherit;font-size:12px;font-weight:600;color:var(--ink);cursor:pointer;white-space:nowrap;}
.rqc.rqc-v2 .rc2-btn:hover{border-color:var(--vio);color:var(--vio-txt);}
.rqc.rqc-v2 .rc2-lnkbtn{border:0;background:transparent;display:inline-flex;align-items:center;gap:4px;font:inherit;font-size:12px;font-weight:600;color:var(--ink);cursor:pointer;white-space:nowrap;padding:4px 0;}
.rqc.rqc-v2 .rc2-lnkbtn:hover{color:var(--vio-txt);}
.rqc.rqc-v2 .rc2-rail{position:relative;align-self:stretch;}
.rqc.rqc-v2 .rc2-rail::before{content:"";position:absolute;left:11px;top:0;bottom:0;border-left:1px solid var(--mute);}
.rqc.rqc-v2 .rc2-vis{font-family:var(--mono);font-size:11px;color:var(--faint);margin-left:6px;}
.rqc.rqc-v2 .rc-pager{display:flex;align-items:center;gap:12px;padding:10px 16px;border-top:1px solid var(--hair);background:var(--softer);flex-wrap:wrap;}
.rqc.rqc-v2 .rc-pgcount{font-size:11.5px;color:var(--sub);font-variant-numeric:tabular-nums;}
.rqc.rqc-v2 .rc-pgright{margin-left:auto;display:flex;align-items:center;gap:14px;}
.rqc.rqc-v2 .rc-rpp{font-size:11.5px;color:var(--sub);display:inline-flex;align-items:center;gap:6px;}
.rqc.rqc-v2 .rc-rpp select{font:inherit;font-size:11.5px;border:1px solid var(--hair);border-radius:7px;padding:3px 6px;background:#fff;color:var(--ink);cursor:pointer;}
.rqc.rqc-v2 .rc-pgbtns{display:inline-flex;align-items:center;gap:4px;}
.rqc.rqc-v2 .rc-pgbtns button{min-width:26px;height:26px;border:1px solid var(--hair);background:#fff;border-radius:7px;font:inherit;font-size:12px;color:var(--ink);cursor:pointer;padding:0 6px;}
.rqc.rqc-v2 .rc-pgbtns button.on{border-color:var(--vio);color:var(--vio-txt);font-weight:700;}
.rqc.rqc-v2 .rc-pgbtns button:disabled{opacity:.4;cursor:default;}
.rqc.rqc-v2 .rc-pgbtns button:not(:disabled):not(.on):hover{border-color:var(--mute);}
`;
